// src/components/ReadingTable.js
// Enhanced Reading Table with Sensor Actions, Linking, Online/Offline TTL State, Row Memoization, and Active Entity Highlighting
// --- UPDATE: V2 — fleet summary strip, search, status / link filters, sortable columns, PSI gauge + live
// sparkline + delta, ticking relative ages (TTL re-evaluated every 30s), density toggle, CSV export
// (all / filtered), copy device ID, keyboard row activation, incremental rendering, persisted preferences.
// --- END UPDATE
// --- UPDATE: V3 — live status + FlowSense AI + performance
//   • Rows come from a sticky registry (sensors ∪ devices sending readings, case-insensitive),
//     so a sensor no longer shows from live readings and then disappears when the list loads.
//   • Live state per sensor: Transmitting / Online / Delayed / Offline / No data, from the time
//     data was last heard (arrival in this browser, received_at/timestamp, last_activity_at).
//     A WebSocket drop shows a banner and no longer turns every sensor "offline".
//   • A buffered reading replayed by a node (old timestamp) counts as "heard now";
//     an older REST reading can't replace a newer live one.
//   • FlowSense AI column + popover: linked-asset health / RUL (sensor.ai_predictions from
//     GET /api/v1/sensors) and an edge anomaly check (spike / drop) on the PSI actually received.
//   • Stable default order (online first, then ID) — rows no longer jump on every reading.
//   • Performance: shared 5 s clock, rows re-render only when their own data / age label
//     changes, stable callbacks, lazy menus/popovers, SortableTh hoisted out of render.
// --- END UPDATE

import React, { useMemo, useState, memo } from 'react';
// --- UPDATE
import { useEffect, useRef, useCallback } from 'react';
// --- END UPDATE
import {
  Table, Thead, Tbody, Tr, Th, Td, Badge, Text, HStack,
  IconButton, Tooltip, Menu, MenuButton, MenuList, MenuItem,
  Icon, Box, useColorModeValue, Tabs, TabList, Tab, Flex,
  // --- UPDATE
  Input, InputGroup, InputLeftElement, InputRightElement, Select, Button,
  SimpleGrid, Progress, MenuDivider, useToast, Kbd,
  // --- END UPDATE
  // --- UPDATE: V3
  Skeleton,
  // --- END UPDATE
} from '@chakra-ui/react';
import {
  MdMoreVert, MdLink, MdLinkOff, MdVisibility, MdHistory,
  MdWarning, MdCheckCircle, MdError, MdSensorsOff, MdSensors,
  // --- UPDATE
  MdSearch, MdClose, MdFileDownload, MdFormatLineSpacing, MdContentCopy,
  MdArrowUpward, MdArrowDownward, MdUnfoldMore, MdTrendingUp, MdTrendingDown,
  MdTrendingFlat, MdExpandMore, MdSpeed, MdNotificationsActive,
  // --- END UPDATE
  // --- UPDATE: V3
  MdHelpOutline, MdSync,
  // --- END UPDATE
} from 'react-icons/md';

import { useNotifications } from '../contexts/NotificationContext';
// --- UPDATE: V3 shared live-status + AI modules
import { useLiveSensorRegistry, useNow, useRollingValues } from './sensors/useLiveSensorRegistry';
import { LIVE_STATE_META, formatAgo, normalizeId } from './sensors/sensorLiveStatus';
import { deriveSensorAi } from './sensors/sensorAiInsights';
import { AiFleetStrip, LiveFeedBanner, LiveStatusIndicator, SensorAiBadge, summarizeAi } from './sensors/SensorLiveUi';
// --- END UPDATE

// --- UPDATE: constants & helpers
// --- UPDATE: V3 — 5 s shared clock (was a 30 s per-table interval)
const LIVE_CLOCK_MS       = 5000;
// --- END UPDATE
const DEFAULT_PAGE_SIZE   = 50;
const PSI_HISTORY_MAX     = 24;
const PSI_GAUGE_MAX       = 150; // gauge scale; thresholds below are the existing 20 / 100 PSI rules
const PSI_LOW             = 20;
const PSI_HIGH            = 100;
const PREF_KEY_DEFAULT    = 'flowsense.readingTable.v2';

const loadPrefs = (key) => {
  try {
    if (typeof window === 'undefined' || !key) return {};
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) || {} : {};
  } catch {
    return {};
  }
};

const savePrefs = (key, value) => {
  try {
    if (typeof window !== 'undefined' && key) window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — preferences are simply not persisted */
  }
};

const csvEscape = (value) => {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Same decision tree as getStatusBadge, returned as a category for filtering / counting:
 * offline | critical (red) | warning (orange) | normal (green) | unknown
 */
const getStatusCategory = (psi, level, isOnline) => {
  if (!isOnline) return 'offline';
  if (level !== undefined && level !== null && level !== '') {
    const levelStr = String(level).toUpperCase();
    if (levelStr === 'HIGH' || levelStr === 'EMPTY') return 'critical';
    if (levelStr === 'LOW' || levelStr === 'MEDIUM') return 'warning';
    return 'normal';
  }
  if (psi !== undefined && psi !== null) {
    if (psi > PSI_HIGH) return 'critical';
    if (psi < PSI_LOW) return 'warning';
    return 'normal';
  }
  return 'unknown';
};

const STATUS_RANK = { critical: 4, warning: 3, unknown: 2, normal: 1, offline: 0 };

const SORT_COLUMNS = {
  device:  (s) => String(s.deviceId ?? '').toLowerCase(),
  reading: (s) => (s.psi !== null && s.psi !== undefined ? s.psi : -Infinity),
  status:  (s) => STATUS_RANK[getStatusCategory(s.psi, s.level, s.isOnline)] ?? 0,
  quality: (s) => (Number.isFinite(Number(s.quality)) ? Number(s.quality) : -1),
  link:    (s) => (s.__pipelineId ? 1 : 0),
  // --- UPDATE: V3 — last heard (not the device timestamp), live state and AI score
  updated: (s) => s.lastHeardMs || 0,
  live:    (s) => (LIVE_STATE_META[s.liveState]?.rank ?? 0) * 1e13 + (s.lastHeardMs || 0),
  ai:      (s) => (s.__ai ? s.__ai.score : -1),
  // --- END UPDATE
};

// --- UPDATE: V3 helpers
/** Default order: online first, then device ID. Stable, so rows don't jump on every reading. */
const defaultOrder = (a, b) => {
  if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
  return String(a.deviceId).localeCompare(String(b.deviceId), undefined, { numeric: true, sensitivity: 'base' });
};

const extractPsi = (liveData, sensor) => {
  const raw = liveData?.psi ?? liveData?.pressure ?? liveData?.value
    ?? sensor?.psi ?? sensor?.pressure ?? sensor?.value
    ?? (sensor?.latest_reading?.kind === 'pressure' ? sensor.latest_reading.value : undefined);
  if (raw === undefined || raw === null || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
};

const psiOfRow = (row) => extractPsi(row.reading, row.sensor);

/** Stable function identity that always calls the latest prop (keeps memoised rows memoised). */
const useStableCallback = (fn) => {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...args) => (ref.current ? ref.current(...args) : undefined), []);
};
// --- END UPDATE

/** Tiny dependency-free sparkline; colour follows currentColor. */
const PsiSparkline = memo(({ values = [], width = 64, height = 18 }) => {
  const d = useMemo(() => {
    if (!values || values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const step = (width - 2) / (values.length - 1);
    return values
      .map((v, i) => `${i ? 'L' : 'M'}${(1 + i * step).toFixed(1)},${(height - 1 - ((v - min) / range) * (height - 2)).toFixed(1)}`)
      .join(' ');
  }, [values, width, height]);
  if (!d) return null;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Recent pressure trend" style={{ display: 'block' }}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
});
PsiSparkline.displayName = 'PsiSparkline';
// --- END UPDATE

// --- UPDATE: V3 — hoisted out of the render function (it was re-created on every render,
// which remounted every header cell)
const SortableTh = memo(({ colKey, children, isNumeric, sortBy, sortDir, onSort, activeColor, ...rest }) => {
  const active = sortBy === colKey;
  const SortIcon = !active ? MdUnfoldMore : sortDir === 'asc' ? MdArrowUpward : MdArrowDownward;
  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      color={active ? activeColor : undefined}
      onClick={() => onSort(colKey)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      _hover={{ color: activeColor }}
      {...rest}
    >
      <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'} display="inline-flex">
        <Text as="span">{children}</Text>
        <Icon as={SortIcon} boxSize={3} opacity={active ? 1 : 0.4} />
      </HStack>
    </Th>
  );
});
SortableTh.displayName = 'SortableTh';
// --- END UPDATE

// =========================================================================
// PURE UI HELPERS
// =========================================================================

// --- UPDATE: V3 — offline tooltip now says how long ago data was last received
const getStatusBadge = (psi, level, isOnline, ageLabel) => {
  if (!isOnline) {
    return (
      <Tooltip label={`No data received for over 5 minutes${ageLabel ? ` (last ${ageLabel})` : ''}. Showing the last known reading.`} hasArrow>
        {/* --- END UPDATE */}
        <Badge colorScheme="gray" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdSensorsOff} />
          Offline
        </Badge>
      </Tooltip>
    );
  }

  if (level !== undefined && level !== null && level !== '') {
    const levelStr = String(level).toUpperCase();
    if (levelStr === 'HIGH' || levelStr === 'EMPTY') {
      return (
        <Badge colorScheme="red" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdWarning} /> {levelStr}
        </Badge>
      );
    } else if (levelStr === 'LOW' || levelStr === 'MEDIUM') {
      return (
        <Badge colorScheme="orange" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdWarning} /> {levelStr}
        </Badge>
      );
    } else {
      return (
        <Badge colorScheme="green" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdCheckCircle} /> {levelStr}
        </Badge>
      );
    }
  }

  if (psi !== undefined && psi !== null) {
    if (psi > 100) {
      return (
        <Badge colorScheme="red" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdWarning} /> High
        </Badge>
      );
    } else if (psi < 20) {
      return (
        <Badge colorScheme="orange" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdWarning} /> Low
        </Badge>
      );
    } else {
      return (
        <Badge colorScheme="green" display="flex" alignItems="center" gap={1} width="max-content">
          <Icon as={MdCheckCircle} /> Normal
        </Badge>
      );
    }
  }

  return <Badge colorScheme="gray">Unknown</Badge>;
};

const getQualityIndicator = (quality, isOnline) => {
  if (!quality) return <Text color="gray.400" fontSize="sm">--</Text>;

  let colorScheme = 'green';
  if (quality < 70) colorScheme = 'orange';
  if (quality < 40) colorScheme = 'red';
  if (!isOnline) colorScheme = 'gray';

  return (
    <Tooltip label={`Signal Quality: ${quality}% ${!isOnline ? '(Last Known)' : ''}`}>
      <Badge colorScheme={colorScheme} variant="subtle">
        {quality}%
      </Badge>
    </Tooltip>
  );
};

// =========================================================================
// MEMOIZED SENSOR ROW (With Highlight State)
// =========================================================================
const SensorTableRow = memo(({
  sensor,
  pipelineId,
  hoverBg,
  isSelected,
  selectedBg,
  onSensorSelect,
  onLinkSensor,
  onUnlinkSensor,
  onViewHistory,
  // --- UPDATE: optional row props
  psiTrend,       // recent PSI values for this sensor (oldest → newest)
  onCopyId,
  gaugeTrackBg,
  // --- END UPDATE
  // --- UPDATE: V3 — ageLabel changes at most every 10 s, so a row re-renders only when
  // its own data or its age label changes (nowTick re-rendered every row every tick)
  ageLabel,
  ai,
  thresholds,
  // --- END UPDATE
}) => {
  const isLinked = !!pipelineId;

  const getLinkStatus = () => {
    if (isLinked) {
      return (
        <Tooltip label={`Linked to Pipeline ${pipelineId}`}>
          <Badge colorScheme="purple" variant="subtle" px={2} py={1} borderRadius="md" display="flex" alignItems="center" gap={1} width="max-content">
            <Icon as={MdLink} />
            Pipeline #{pipelineId}
          </Badge>
        </Tooltip>
      );
    }
    return (
      <Badge colorScheme="gray" variant="outline" width="max-content">
        NOT LINKED
      </Badge>
    );
  };

  // --- UPDATE: derived PSI visuals
  const hasPsi = sensor.psi !== undefined && sensor.psi !== null;
  const prevPsi = psiTrend && psiTrend.length > 1 ? psiTrend[psiTrend.length - 2] : null;
  const psiDelta = hasPsi && prevPsi !== null ? sensor.psi - prevPsi : null;
  const gaugeColor = !sensor.isOnline ? 'gray' : sensor.psi > PSI_HIGH ? 'red' : sensor.psi < PSI_LOW ? 'orange' : 'green';
  const DeltaIcon = psiDelta === null || Math.abs(psiDelta) < 0.05 ? MdTrendingFlat : psiDelta > 0 ? MdTrendingUp : MdTrendingDown;
  // --- END UPDATE
  // --- UPDATE: V3 — measured vs heard
  const measuredAt = sensor.measuredMs ? new Date(sensor.measuredMs).toLocaleString() : null;
  const heardAt = sensor.lastHeardMs ? new Date(sensor.lastHeardMs).toLocaleString() : null;
  const timeTooltip = !measuredAt && !heardAt
    ? 'No reading received yet'
    : `Measured ${measuredAt || '—'} · last data received ${heardAt || '—'}`;
  const liveMeta = LIVE_STATE_META[sensor.liveState] || LIVE_STATE_META.no_data;
  // --- END UPDATE

  return (
    <Tr
      bg={isSelected ? selectedBg : 'transparent'}
      borderLeftWidth={isSelected ? '4px' : '0px'}
      borderLeftColor="blue.400"
      _hover={{ bg: isSelected ? selectedBg : hoverBg }}
      cursor="pointer"
      onClick={() => onSensorSelect?.(sensor)}
      opacity={sensor.isOnline ? 1 : 0.6}
      transition="background-color 0.15s ease"
      // --- UPDATE: keyboard activation + a11y
      tabIndex={onSensorSelect ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          onSensorSelect?.(sensor);
        }
      }}
      aria-selected={isSelected || undefined}
      _focusVisible={{ outline: '2px solid', outlineColor: 'blue.300', outlineOffset: '-2px' }}
      role="group"
      // --- END UPDATE
      // --- UPDATE: V3
      data-device-id={sensor.deviceId}
      data-live-state={sensor.liveState}
      // --- END UPDATE
    >
      {/* --- UPDATE: V3 — live state column */}
      <Td>
        <LiveStatusIndicator row={sensor.__row} thresholds={thresholds} ageLabel={ageLabel} />
      </Td>
      {/* --- END UPDATE */}
      <Td>
        {/* --- UPDATE: copy affordance */}
        <HStack spacing={1}>
        {/* --- END UPDATE */}
        <Text fontWeight="bold" fontFamily="mono" color={sensor.isOnline ? "blue.500" : "gray.500"}>
          {sensor.deviceId}
        </Text>
        {/* --- UPDATE */}
        {onCopyId && (
          <IconButton
            icon={<MdContentCopy />}
            size="xs" h="18px" minW="18px" variant="ghost"
            opacity={0} _groupHover={{ opacity: 1 }} _focusVisible={{ opacity: 1 }}
            onClick={(e) => { e.stopPropagation(); onCopyId(sensor.deviceId); }}
            aria-label={`Copy ${sensor.deviceId}`}
          />
        )}
        </HStack>
        {/* --- END UPDATE */}
        {sensor.name && <Text fontSize="xs" color="gray.500">{sensor.name}</Text>}
        {/* --- UPDATE: V3 — devices that send readings but aren't in this table's register */}
        {!sensor.registered && (
          <Tooltip
            hasArrow
            label="This device is sending pressure readings but is not in the pressure sensor register (not registered yet, or registered with another type). Register or retype it to link it."
          >
            <Badge colorScheme="yellow" variant="subtle" fontSize="9px" mt={0.5}>
              <HStack spacing={0.5}><Icon as={MdHelpOutline} boxSize={2.5} /><Text>NOT IN REGISTER</Text></HStack>
            </Badge>
          </Tooltip>
        )}
        {sensor.retained && (
          <Tooltip hasArrow label="Missing from the last sensor list refresh — kept on screen while the list reloads.">
            <Badge colorScheme="gray" variant="subtle" fontSize="9px" mt={0.5}>
              <HStack spacing={0.5}><Icon as={MdSync} boxSize={2.5} /><Text>SYNCING</Text></HStack>
            </Badge>
          </Tooltip>
        )}
        {/* --- END UPDATE */}
      </Td>

      <Td isNumeric>
        {/* --- UPDATE: value + delta + sparkline + gauge */}
        <HStack justify="flex-end" spacing={2}>
          {hasPsi && psiTrend && psiTrend.length > 1 && (
            <Box color={`${gaugeColor}.400`} display={{ base: 'none', lg: 'block' }}>
              <PsiSparkline values={psiTrend} />
            </Box>
          )}
        {/* --- END UPDATE */}
        <Text fontWeight="bold" fontSize="lg" color={sensor.isOnline ? "inherit" : "gray.400"}>
          {sensor.psi !== undefined && sensor.psi !== null
            ? `${Number(sensor.psi).toFixed(1)} PSI`
            : (sensor.level ? String(sensor.level).toUpperCase() : '--')}
        </Text>
        {/* --- UPDATE */}
        </HStack>
        {hasPsi && (
          <HStack justify="flex-end" spacing={2} mt={0.5}>
            {psiDelta !== null && (
              <HStack spacing={0.5} color={Math.abs(psiDelta) < 0.05 ? 'gray.400' : psiDelta > 0 ? 'red.400' : 'blue.400'}>
                <Icon as={DeltaIcon} boxSize={3} />
                <Text fontSize="10px" fontWeight="700">
                  {psiDelta > 0 ? '+' : ''}{psiDelta.toFixed(1)}
                </Text>
              </HStack>
            )}
            <Tooltip label={`Scale 0–${PSI_GAUGE_MAX} PSI · normal ${PSI_LOW}–${PSI_HIGH}`} hasArrow>
              <Box w="72px">
                <Progress
                  value={Math.min(100, Math.max(0, (sensor.psi / PSI_GAUGE_MAX) * 100))}
                  size="xs"
                  colorScheme={gaugeColor}
                  bg={gaugeTrackBg}
                  borderRadius="full"
                  aria-label={`${sensor.psi} PSI`}
                />
              </Box>
            </Tooltip>
          </HStack>
        )}
        {/* --- END UPDATE */}
      </Td>

      <Td>
        {/* --- UPDATE: V3 */}
        {getStatusBadge(sensor.psi, sensor.level, sensor.isOnline, ageLabel)}
        {/* --- END UPDATE */}
      </Td>

      {/* --- UPDATE: V3 — FlowSense AI */}
      <Td onClick={(e) => e.stopPropagation()}>
        {ai && <SensorAiBadge ai={ai} kind="pressure" isOnline={sensor.isOnline} />}
      </Td>
      {/* --- END UPDATE */}

      <Td>
        {getQualityIndicator(sensor.quality, sensor.isOnline)}
      </Td>

      <Td>
        {getLinkStatus()}
      </Td>

      <Td>
        {/* --- UPDATE: V3 — relative age of the last data received + measured time on hover */}
        <Tooltip label={timeTooltip} hasArrow openDelay={300}>
          <Box>
            <Text fontSize="sm" color={sensor.isOnline ? 'gray.500' : 'gray.400'} fontWeight={sensor.isOnline ? '600' : 'normal'}>
              {ageLabel || 'No Recent Data'}
            </Text>
            <Text fontSize="9px" fontWeight="800" letterSpacing="0.05em" color={`${liveMeta.color}.500`}>
              {liveMeta.label.toUpperCase()}
            </Text>
          </Box>
        </Tooltip>
        {/* --- END UPDATE */}
      </Td>

      <Td onClick={(e) => e.stopPropagation()}>
        {/* --- UPDATE: V3 — lazy menu: the list mounts only when opened */}
        <Menu isLazy>
        {/* --- END UPDATE */}
          <MenuButton
            as={IconButton} icon={<Icon as={MdMoreVert} />} size="sm" variant="ghost"
            // --- UPDATE
            aria-label={`Actions for ${sensor.deviceId}`}
            // --- END UPDATE
          />
          <MenuList zIndex={10}>
            <MenuItem icon={<Icon as={MdVisibility} />} onClick={() => onSensorSelect?.(sensor)}>
              View Details
            </MenuItem>

            {!isLinked ? (
              <MenuItem icon={<Icon as={MdLink} />} color="purple.500" fontWeight="500" onClick={() => onLinkSensor?.(sensor)}>
                Link to Pipeline
              </MenuItem>
            ) : (
              <MenuItem icon={<Icon as={MdLinkOff} />} color="red.500" fontWeight="500" onClick={() => onUnlinkSensor?.(sensor.deviceId)}>
                Unlink from Pipeline
              </MenuItem>
            )}

            <MenuItem icon={<Icon as={MdHistory} />} onClick={() => onViewHistory?.(sensor.deviceId)}>
              View History
            </MenuItem>
            {/* --- UPDATE */}
            {onCopyId && (
              <>
                <MenuDivider />
                <MenuItem icon={<Icon as={MdContentCopy} />} onClick={() => onCopyId(sensor.deviceId)}>
                  Copy Device ID
                </MenuItem>
              </>
            )}
            {/* --- END UPDATE */}
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
});
// --- UPDATE: V3
SensorTableRow.displayName = 'SensorTableRow';
// --- END UPDATE

function ReadingTable({
  sensors = [],
  readings = {},
  sensorPipelineLinks = {},
  selectedSensorId = null, // 🚀 Highlighting prop
  onSensorSelect,
  onLinkSensor,
  onUnlinkSensor,
  onViewHistory,
  // --- UPDATE: optional props
  pageSize = DEFAULT_PAGE_SIZE,
  persistKey = PREF_KEY_DEFAULT, // pass null to disable persistence
  showSummary = true,
  // --- END UPDATE
  // --- UPDATE: V3 optional props
  liveThresholds = null,       // overrides for DEFAULT_LIVE_THRESHOLDS (ms)
  includeUnregistered = true,  // show devices sending readings that are not in `sensors`
  isLoading = false,
  // --- END UPDATE
}) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.800');
  const selectedBg = useColorModeValue('blue.50', 'rgba(49, 130, 206, 0.18)');
  // --- UPDATE: hoisted colour tokens
  const cardBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const gaugeTrackBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const subtle = useColorModeValue('gray.500', 'gray.400');
  const thActiveColor = useColorModeValue('blue.600', 'blue.300');
  // --- END UPDATE

  const [filterTab, setFilterTab] = useState(0);

  const { isConnected } = useNotifications();

  // --- UPDATE: V2 state
  const toast = useToast();
  const initialPrefs = useRef(null);
  if (initialPrefs.current === null) initialPrefs.current = loadPrefs(persistKey);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // all | critical | warning | normal | unknown
  const [linkFilter, setLinkFilter] = useState('all');       // all | linked | unlinked
  const [sortBy, setSortBy] = useState(() => initialPrefs.current.sortBy || 'default');
  const [sortDir, setSortDir] = useState(() => initialPrefs.current.sortDir || 'desc');
  const [density, setDensity] = useState(() => initialPrefs.current.density || 'comfortable');
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const searchRef = useRef(null);
  const deferredSearch =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    typeof React.useDeferredValue === 'function' ? React.useDeferredValue(search) : search;

  useEffect(() => {
    savePrefs(persistKey, { sortBy, sortDir, density });
  }, [persistKey, sortBy, sortDir, density]);
  // --- END UPDATE

  // --- UPDATE: V3 — live registry replaces the WebSocket-gated 5-minute TTL
  const [aiOnly, setAiOnly] = useState(false);
  const now = useNow(LIVE_CLOCK_MS);
  const registry = useLiveSensorRegistry({ sensors, readings, now, thresholds: liveThresholds, includeUnregistered });

  // Links keyed case-insensitively as well (the page stores them by the sensor's own ID)
  const linkByKey = useMemo(() => {
    const m = new Map();
    Object.keys(sensorPipelineLinks || {}).forEach((k) => m.set(normalizeId(k), sensorPipelineLinks[k]));
    return m;
  }, [sensorPipelineLinks]);
  const pipelineOf = useCallback(
    (s) => sensorPipelineLinks[s.deviceId] ?? linkByKey.get(s.__key),
    [sensorPipelineLinks, linkByKey]
  );

  // Row view-models are cached per registry row object, so an unchanged row keeps its identity.
  const viewCacheRef = useRef(new WeakMap());
  const mergedSensors = useMemo(() => {
    const cache = viewCacheRef.current;
    return registry.rows.map((row) => {
      const hit = cache.get(row);
      if (hit) return hit;
      const sensor = row.sensor || {};
      const liveData = row.reading || {};
      const view = {
        ...sensor,
        deviceId: row.id,
        psi: extractPsi(liveData, sensor),
        level: liveData?.level ?? liveData?.water_level ?? sensor.level ?? sensor.water_level,
        quality: liveData?.quality ?? sensor.quality,
        status: liveData?.status ?? sensor.status,
        timestamp: liveData?.timestamp || liveData?.received_at || sensor.timestamp || sensor.last_seen_at || sensor.updated_at || null,
        measuredMs: row.measuredMs,
        lastHeardMs: row.lastHeardMs,
        liveState: row.liveState,
        isOnline: row.isOnline,
        registered: row.registered,
        retained: row.retained,
        __key: row.key,
        __row: row,
      };
      cache.set(row, view);
      return view;
    }).sort(defaultOrder);
  }, [registry.rows]);

  const onlineCount = registry.counts.online;
  const offlineCount = registry.counts.offline;

  // Rolling PSI history from real arrivals only (replaces the setState-per-reading effect)
  const { store: psiStore } = useRollingValues(registry.rows, psiOfRow, PSI_HISTORY_MAX);

  // FlowSense AI per sensor — recomputed only when that sensor's inputs change
  const aiCacheRef = useRef(new Map());
  const aiByKey = useMemo(() => {
    const cache = aiCacheRef.current;
    const out = new Map();
    mergedSensors.forEach((s) => {
      const values = psiStore.get(s.__key)?.values || null;
      const prev = cache.get(s.__key);
      if (prev && prev.view === s && prev.values === values) {
        out.set(s.__key, prev.ai);
        return;
      }
      const ai = deriveSensorAi({ kind: 'pressure', sensor: s, prediction: null, psiValues: values || [] });
      cache.set(s.__key, { view: s, values, ai });
      out.set(s.__key, ai);
    });
    cache.forEach((_, k) => { if (!out.has(k)) cache.delete(k); });
    return out;
  }, [mergedSensors, psiStore]);

  const aiSummary = useMemo(() => summarizeAi([...aiByKey.values()]), [aiByKey]);
  const lastArrivalMs = useMemo(
    () => registry.rows.reduce((m, r) => (r.lastArrivalMs && r.lastArrivalMs > m ? r.lastArrivalMs : m), 0) || null,
    [registry.rows]
  );
  // --- END UPDATE

  // --- UPDATE: fleet summary (V2)
  const summary = useMemo(() => {
    let critical = 0;
    let warning = 0;
    let linked = 0;
    let psiSum = 0;
    let psiN = 0;
    let qualitySum = 0;
    let qualityN = 0;
    const statusCounts = { critical: 0, warning: 0, normal: 0, unknown: 0, offline: 0 };
    mergedSensors.forEach((s) => {
      const cat = getStatusCategory(s.psi, s.level, s.isOnline);
      statusCounts[cat] += 1;
      if (cat === 'critical') critical += 1;
      if (cat === 'warning') warning += 1;
      // --- UPDATE: V3 — case-insensitive link lookup
      if (pipelineOf(s)) linked += 1;
      // --- END UPDATE
      if (s.isOnline && s.psi !== null && s.psi !== undefined) {
        psiSum += s.psi;
        psiN += 1;
      }
      const q = Number(s.quality);
      if (s.isOnline && Number.isFinite(q) && q > 0) {
        qualitySum += q;
        qualityN += 1;
      }
    });
    return {
      critical,
      warning,
      statusCounts,
      linked,
      linkedPct: mergedSensors.length ? Math.round((linked / mergedSensors.length) * 100) : 0,
      avgPsi: psiN ? psiSum / psiN : null,
      avgQuality: qualityN ? Math.round(qualitySum / qualityN) : null,
    };
  }, [mergedSensors, pipelineOf]);
  // --- END UPDATE

  const displaySensors = useMemo(() => {
    // --- UPDATE: tab filter (unchanged) followed by search / status / link filters and optional sort
    let list;
    if (filterTab === 1) list = mergedSensors.filter(s => s.isOnline);
    else if (filterTab === 2) list = mergedSensors.filter(s => !s.isOnline);
    else list = mergedSensors;

    const q = deferredSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((s) =>
        String(s.deviceId ?? '').toLowerCase().includes(q) ||
        String(s.name ?? '').toLowerCase().includes(q) ||
        // --- UPDATE: V3
        String(pipelineOf(s) ?? '').toLowerCase().includes(q)
        // --- END UPDATE
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((s) => getStatusCategory(s.psi, s.level, s.isOnline) === statusFilter);
    }
    if (linkFilter !== 'all') {
      // --- UPDATE: V3
      list = list.filter((s) => (linkFilter === 'linked') === !!pipelineOf(s));
      // --- END UPDATE
    }
    // --- UPDATE: V3 — AI flagged filter
    if (aiOnly) list = list.filter((s) => aiByKey.get(s.__key)?.flagged || aiByKey.get(s.__key)?.level === 'warning');
    // --- END UPDATE
    if (sortBy !== 'default' && SORT_COLUMNS[sortBy]) {
      const getter = SORT_COLUMNS[sortBy];
      // --- UPDATE: V3
      const withLinks = list.map((s) => ({ s, v: getter({ ...s, __pipelineId: pipelineOf(s), __ai: aiByKey.get(s.__key) }) }));
      // --- END UPDATE
      withLinks.sort((a, b) => {
        if (a.v === b.v) return defaultOrder(a.s, b.s);
        const cmp = a.v > b.v ? 1 : -1;
        return sortDir === 'asc' ? cmp : -cmp;
      });
      list = withLinks.map((x) => x.s);
    }
    return list;
  }, [mergedSensors, filterTab, deferredSearch, statusFilter, linkFilter, sortBy, sortDir, pipelineOf, aiOnly, aiByKey]);
  // --- END UPDATE

  // --- UPDATE: paging, handlers, export
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [filterTab, deferredSearch, statusFilter, linkFilter, sortBy, sortDir, pageSize, aiOnly]);

  const pagedSensors = useMemo(() => displaySensors.slice(0, visibleCount), [displaySensors, visibleCount]);
  const remaining = displaySensors.length - pagedSensors.length;
  const hasActiveFilters = !!search || statusFilter !== 'all' || linkFilter !== 'all' || filterTab !== 0 || aiOnly;

  const handleSort = useCallback((key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'device' ? 'asc' : 'desc');
    }
  }, [sortBy]);

  const handleCopyId = useCallback(async (id) => {
    try {
      await navigator.clipboard.writeText(String(id));
      toast({ title: 'Copied', description: String(id), status: 'success', duration: 1500, position: 'bottom-right' });
    } catch {
      toast({ title: 'Copy failed', description: 'Clipboard is unavailable in this browser.', status: 'warning', duration: 2500, position: 'bottom-right' });
    }
  }, [toast]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setLinkFilter('all');
    setFilterTab(0);
    // --- UPDATE: V3
    setAiOnly(false);
    // --- END UPDATE
  }, []);

  // --- UPDATE: V3 — stable callbacks so memoised rows stay memoised even if the page
  // passes inline functions
  const stableSelect = useStableCallback(onSensorSelect);
  const stableLink = useStableCallback(onLinkSensor);
  const stableUnlink = useStableCallback(onUnlinkSensor);
  const stableHistory = useStableCallback(onViewHistory);
  const selectedKey = selectedSensorId !== null && selectedSensorId !== undefined ? normalizeId(selectedSensorId) : null;
  // --- END UPDATE

  const exportCSV = useCallback((scope = 'all') => {
    const source = scope === 'filtered' ? displaySensors : mergedSensors;
    // --- UPDATE: V3 — live state, last heard and AI columns
    const header = ['Device ID', 'Name', 'PSI', 'Level', 'Status', 'Quality %', 'Online', 'Live state', 'Registered', 'Pipeline', 'Measured (ISO)', 'Last data received (ISO)', 'AI level', 'AI anomaly', 'Asset health score', 'Asset RUL days'];
    const rows = source.map((s) => {
      const ai = aiByKey.get(s.__key);
      return [
        s.deviceId,
        s.name || '',
        s.psi !== null && s.psi !== undefined ? s.psi.toFixed(2) : '',
        s.level ? String(s.level).toUpperCase() : '',
        getStatusCategory(s.psi, s.level, s.isOnline),
        s.quality ?? '',
        s.isOnline ? 'yes' : 'no',
        s.liveState,
        s.registered ? 'yes' : 'no',
        pipelineOf(s) ?? '',
        s.measuredMs ? new Date(s.measuredMs).toISOString() : '',
        s.lastHeardMs ? new Date(s.lastHeardMs).toISOString() : '',
        ai?.level ?? '',
        ai?.anomaly ? ai.anomaly.label : '',
        ai?.health?.score ?? '',
        ai?.health?.rulDays ?? '',
      ];
    });
    // --- END UPDATE
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sensor-readings-${scope}-${Date.now()}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast({ title: 'Exported', description: `${rows.length} rows`, status: 'success', duration: 2000, position: 'bottom-right' });
  }, [displaySensors, mergedSensors, pipelineOf, aiByKey, toast]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if (e.key === '/' && !typing && searchRef.current) {
        e.preventDefault();
        searchRef.current.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const summaryItems = [
    // --- UPDATE: V3 — online card shows how many are transmitting right now
    { label: 'Online', value: `${onlineCount}/${mergedSensors.length}`, icon: MdSensors, color: 'green.500', hint: `${registry.counts.transmitting} transmitting now` },
    // --- END UPDATE
    { label: 'Critical', value: summary.critical, icon: MdNotificationsActive, color: summary.critical ? 'red.500' : undefined, filter: 'critical' },
    { label: 'Warning', value: summary.warning, icon: MdWarning, color: summary.warning ? 'orange.400' : undefined, filter: 'warning' },
    { label: 'Avg pressure', value: summary.avgPsi !== null ? `${summary.avgPsi.toFixed(1)} PSI` : '—', icon: MdSpeed, color: 'blue.500' },
    { label: 'Linked', value: `${summary.linkedPct}%`, icon: MdLink, color: 'purple.500', hint: `${summary.linked} of ${mergedSensors.length}` },
  ];
  // --- END UPDATE

  // --- UPDATE: V3 — shared header props
  const thProps = { sortBy, sortDir, onSort: handleSort, activeColor: thActiveColor };
  // --- END UPDATE

  return (
    <Box>
      {/* --- UPDATE: V3 — feed state (never changes a sensor's own status) */}
      {mergedSensors.length > 0 && (
        <LiveFeedBanner isConnected={isConnected} counts={registry.counts} lastArrivalMs={lastArrivalMs} now={now} />
      )}
      {/* --- END UPDATE */}

      {/* --- UPDATE: fleet summary strip (Critical / Warning cards act as filters) */}
      {showSummary && mergedSensors.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
          {summaryItems.map((item) => {
            const isActive = item.filter && statusFilter === item.filter;
            return (
              <Box
                key={item.label}
                as={item.filter ? 'button' : 'div'}
                textAlign="left"
                p={3}
                bg={cardBg}
                borderWidth="1px"
                borderColor={isActive ? 'blue.400' : borderColor}
                borderRadius="xl"
                cursor={item.filter ? 'pointer' : 'default'}
                transition="border-color 0.15s ease, transform 0.15s ease"
                _hover={item.filter ? { transform: 'translateY(-1px)' } : undefined}
                onClick={item.filter ? () => setStatusFilter(isActive ? 'all' : item.filter) : undefined}
                aria-pressed={item.filter ? !!isActive : undefined}
              >
                <HStack justify="space-between">
                  <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color={subtle}>
                    {item.label}
                  </Text>
                  <Icon as={item.icon} color={item.color || subtle} boxSize={4} />
                </HStack>
                <Text fontSize="xl" fontWeight="800" color={item.color}>
                  {item.value}
                </Text>
                {item.hint && <Text fontSize="10px" color={subtle}>{item.hint}</Text>}
              </Box>
            );
          })}
        </SimpleGrid>
      )}
      {/* --- END UPDATE */}

      {/* --- UPDATE: V3 — FlowSense AI fleet strip (toggles the AI filter) */}
      {mergedSensors.length > 0 && (
        <AiFleetStrip summary={aiSummary} active={aiOnly} onToggle={() => setAiOnly((v) => !v)} />
      )}
      {/* --- END UPDATE */}

      <Flex mb={4} justify="space-between" align="center" wrap="wrap" gap={4}>
        <Tabs variant="soft-rounded" colorScheme="blue" index={filterTab} onChange={(idx) => setFilterTab(idx)}>
          <TabList>
            <Tab fontSize="sm" fontWeight="600">All ({mergedSensors.length})</Tab>
            <Tab fontSize="sm" fontWeight="600" color="green.500">
              <Icon as={MdSensors} mr={1}/> Online ({onlineCount})
            </Tab>
            <Tab fontSize="sm" fontWeight="600" color="gray.500">
              <Icon as={MdSensorsOff} mr={1}/> Offline ({offlineCount})
            </Tab>
          </TabList>
        </Tabs>

        {/* --- UPDATE: toolbar */}
        <HStack spacing={2} flexWrap="wrap" rowGap={2}>
          <InputGroup size="sm" w={{ base: '100%', sm: '220px' }}>
            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
            <Input
              ref={searchRef}
              placeholder="Search ID, name, pipeline…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="lg"
              bg={inputBg}
              aria-label="Search sensors (press / to focus)"
            />
            <InputRightElement>
              {search ? (
                <IconButton size="xs" variant="ghost" icon={<MdClose />} onClick={() => setSearch('')} aria-label="Clear search" />
              ) : (
                <Kbd fontSize="9px" display={{ base: 'none', md: 'inline-block' }}>/</Kbd>
              )}
            </InputRightElement>
          </InputGroup>

          <Select size="sm" w="150px" borderRadius="lg" bg={inputBg} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
            <option value="all">All statuses</option>
            <option value="critical">Critical ({summary.statusCounts.critical})</option>
            <option value="warning">Warning ({summary.statusCounts.warning})</option>
            <option value="normal">Normal ({summary.statusCounts.normal})</option>
            <option value="unknown">Unknown ({summary.statusCounts.unknown})</option>
            <option value="offline">Offline ({summary.statusCounts.offline})</option>
          </Select>

          <Select size="sm" w="135px" borderRadius="lg" bg={inputBg} value={linkFilter} onChange={(e) => setLinkFilter(e.target.value)} aria-label="Filter by link status">
            <option value="all">All links</option>
            <option value="linked">Linked</option>
            <option value="unlinked">Not linked</option>
          </Select>

          {hasActiveFilters && (
            <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<MdClose />} onClick={clearFilters}>
              Clear
            </Button>
          )}

          <Tooltip label={density === 'compact' ? 'Comfortable rows' : 'Compact rows'} hasArrow>
            <IconButton
              size="sm"
              variant={density === 'compact' ? 'solid' : 'ghost'}
              icon={<MdFormatLineSpacing />}
              onClick={() => setDensity((d) => (d === 'compact' ? 'comfortable' : 'compact'))}
              aria-label="Toggle row density"
            />
          </Tooltip>

          {/* --- UPDATE: V3 — lazy */}
          <Menu isLazy>
          {/* --- END UPDATE */}
            <Tooltip label="Export" hasArrow>
              <MenuButton as={IconButton} size="sm" variant="ghost" colorScheme="blue" icon={<MdFileDownload />} aria-label="Export readings" />
            </Tooltip>
            <MenuList zIndex={20} fontSize="sm">
              <MenuItem icon={<MdFileDownload />} onClick={() => exportCSV('all')}>Export all ({mergedSensors.length})</MenuItem>
              <MenuItem icon={<MdFileDownload />} onClick={() => exportCSV('filtered')}>Export filtered ({displaySensors.length})</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
        {/* --- END UPDATE */}
      </Flex>

      {/* --- UPDATE: V3 — skeleton while the first load is in flight */}
      {isLoading && mergedSensors.length === 0 ? (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={4}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h="28px" mb={i < 3 ? 3 : 0} borderRadius="md" />)}
        </Box>
      ) : displaySensors.length === 0 ? (
      // --- END UPDATE
        <Box p={8} textAlign="center" borderWidth="1px" borderStyle="dashed" borderColor={borderColor} borderRadius="md">
          <Icon as={MdError} boxSize={12} color="gray.400" mb={2} />
          <Text color="gray.500">No sensors found in this category</Text>
          {/* --- UPDATE */}
          {hasActiveFilters && (
            <Button size="xs" mt={3} variant="outline" colorScheme="blue" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          {/* --- END UPDATE */}
        </Box>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderColor={borderColor} borderRadius="xl">
          <Table
            variant="simple" size="sm"
            // --- UPDATE: compact density
            sx={density === 'compact' ? { '& tbody td': { paddingTop: '4px', paddingBottom: '4px' } } : undefined}
            // --- END UPDATE
          >
            <Thead bg={tableHeaderBg}>
              <Tr>
                {/* --- UPDATE: sortable headers (click again to flip; "Default" restores online-first order) */}
                {/* --- UPDATE: V3 — Live + FlowSense AI columns; headers hoisted (thProps) */}
                <SortableTh colKey="live" py={4} {...thProps}>Live</SortableTh>
                <SortableTh colKey="device" {...thProps}>Sensor ID</SortableTh>
                <SortableTh colKey="reading" isNumeric {...thProps}>Current Reading</SortableTh>
                <SortableTh colKey="status" {...thProps}>Status</SortableTh>
                <SortableTh colKey="ai" {...thProps}>FlowSense AI</SortableTh>
                <SortableTh colKey="quality" {...thProps}>Quality</SortableTh>
                <SortableTh colKey="link" {...thProps}>Link Status</SortableTh>
                <SortableTh colKey="updated" {...thProps}>Last Data</SortableTh>
                {/* --- END UPDATE */}
                <Th>
                  {sortBy !== 'default' ? (
                    <Button size="xs" variant="link" fontSize="10px" onClick={() => setSortBy('default')}>
                      Default order
                    </Button>
                  ) : (
                    'Actions'
                  )}
                </Th>
                {/* --- END UPDATE */}
              </Tr>
            </Thead>
            <Tbody>
              {/* --- UPDATE: paged list */}
              {pagedSensors.map((sensor) => {
                // --- END UPDATE
                // --- UPDATE: V3 — case-insensitive highlight; per-row props are primitives or cached objects
                const isSelected = selectedKey !== null && selectedKey === sensor.__key;
                return (
                  <SensorTableRow
                    key={sensor.__key}
                    sensor={sensor}
                    pipelineId={pipelineOf(sensor)}
                    hoverBg={hoverBg}
                    isSelected={isSelected}
                    selectedBg={selectedBg}
                    onSensorSelect={onSensorSelect ? stableSelect : undefined}
                    onLinkSensor={stableLink}
                    onUnlinkSensor={stableUnlink}
                    onViewHistory={stableHistory}
                    psiTrend={psiStore.get(sensor.__key)?.values}
                    onCopyId={handleCopyId}
                    gaugeTrackBg={gaugeTrackBg}
                    ageLabel={formatAgo(sensor.lastHeardMs, now)}
                    ai={aiByKey.get(sensor.__key)}
                    thresholds={registry.thresholds}
                  />
                );
                // --- END UPDATE
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* --- UPDATE: footer + incremental rendering */}
      {displaySensors.length > 0 && (
        <Flex justify="space-between" align="center" mt={2} px={1} gap={3} flexWrap="wrap">
          <Text fontSize="10px" color={subtle}>
            Showing {pagedSensors.length} of {displaySensors.length} matching · {mergedSensors.length} total
            {summary.avgQuality !== null ? ` · avg signal ${summary.avgQuality}%` : ''}
          </Text>
          {remaining > 0 && (
            <Button size="xs" variant="outline" colorScheme="blue" borderRadius="full" leftIcon={<MdExpandMore />}
              onClick={() => setVisibleCount((c) => c + pageSize)}
            >
              Load {Math.min(pageSize, remaining)} more ({remaining} remaining)
            </Button>
          )}
        </Flex>
      )}
      {/* --- END UPDATE */}
    </Box>
  );
}

// --- UPDATE: V3 — exported for tests
export { getStatusCategory, defaultOrder };
// --- END UPDATE
export default ReadingTable;
